import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getApplicationSettings } from 'owa-application-settings';
import { isOfflineSyncEnabled } from './isOfflineSyncEnabled';
import { getIsDataWorkerFaulted, getIsOfflineDataFaulted, getSessionId } from 'owa-config';
import { getItem } from 'owa-local-storage';
import {
    getFallbackToNOPGALInSessionStorageKey,
    getFallbackToNOPGALLocalStorageKey,
} from './utils/toggleOfflineNoPGAL';
import { isMailboxSharedOrDelegate } from 'owa-account-shared-mailbox-utils';

export type PDVStateDetails = {
    isOfflinePeopleDataViaPDVEnabledAllUp?: boolean;
    isOfflineSyncEnabled?: boolean;
    isPdvInMemoryEnabled?: boolean;
    isPdvIdbRepositoryEnabled?: boolean;
    isPdvEnabled?: boolean;
    isDataWorkerFaulted?: boolean;
    isOfflineDataFaulted?: boolean;
    isAccountFallbackToNoPGAL?: boolean;
    isSessionFallbackToNoPGAL?: boolean;
    isOfflineTemplateEnabled?: boolean;
    isMailboxSharedOrDelegate?: boolean;
};

export function isOfflinePeopleDataViaPDVEnabled(
    viewerMailboxInfo: MailboxInfo,
    pdvStateDetails?: PDVStateDetails
): boolean {
    // account check for fallback to no pgal sync module
    const fallbackToNoPGAL = getItem(self, getFallbackToNOPGALLocalStorageKey(viewerMailboxInfo));
    const isAccountFallbackToNoPGAL: boolean =
        !!fallbackToNoPGAL && fallbackToNoPGAL.toLocaleLowerCase() == 'true'; // this logic because its a string value

    // session check for fallback to online
    const fallbackToNoPGALSession: string =
        getItem(self, getFallbackToNOPGALInSessionStorageKey(viewerMailboxInfo)) || '';
    const isSessionFallbackToNoPGAL: boolean =
        !!fallbackToNoPGALSession && getSessionId() === fallbackToNoPGALSession;

    // We are intentionally not passing in MailboxInfo to below
    // flight checks because framework sync currently is
    // completely conditioned on the global settings mailbox account's
    // flights since they have some trouble at this time
    // to completely flight offline by account.
    const isOfflineSyncEnabledFlag = isOfflineSyncEnabled(viewerMailboxInfo);
    const isPdvInMemoryEnabledFlag = isGlobalFeatureEnabled('peo-online-via-pdv');
    const isPdvIdbRepositoryEnabledFlag =
        getApplicationSettings('PDV', viewerMailboxInfo).peoEnableIdbRepository &&
        isOfflineSyncEnabledFlag;

    const isPdvEnabledFlag =
        isGlobalFeatureEnabled('peo-offline-via-pdv') || isPdvInMemoryEnabledFlag;
    const isDataWorkerFaultedFlag = getIsDataWorkerFaulted();
    const isOfflineDataFaultedFlag = getIsOfflineDataFaulted();
    const isMailboxSharedOrDelegateFlag = isMailboxSharedOrDelegate(viewerMailboxInfo);
    const isOfflinePeopleDataViaPDVEnabledAllUp =
        isOfflineSyncEnabledFlag &&
        !isAccountFallbackToNoPGAL &&
        !isSessionFallbackToNoPGAL &&
        isPdvEnabledFlag &&
        !isDataWorkerFaultedFlag &&
        !isOfflineDataFaultedFlag &&
        !isMailboxSharedOrDelegateFlag; // we ignore offline mode for shared/delegate mailbox

    if (pdvStateDetails) {
        pdvStateDetails.isOfflinePeopleDataViaPDVEnabledAllUp =
            isOfflinePeopleDataViaPDVEnabledAllUp;
        pdvStateDetails.isOfflineSyncEnabled = isOfflineSyncEnabledFlag;
        pdvStateDetails.isPdvEnabled = isPdvEnabledFlag;
        pdvStateDetails.isPdvInMemoryEnabled = isPdvInMemoryEnabledFlag;
        pdvStateDetails.isPdvIdbRepositoryEnabled = isPdvIdbRepositoryEnabledFlag;
        pdvStateDetails.isDataWorkerFaulted = isDataWorkerFaultedFlag;
        pdvStateDetails.isOfflineDataFaulted = isOfflineDataFaultedFlag;
        pdvStateDetails.isAccountFallbackToNoPGAL = isAccountFallbackToNoPGAL;
        pdvStateDetails.isSessionFallbackToNoPGAL = isSessionFallbackToNoPGAL;
        pdvStateDetails.isMailboxSharedOrDelegate = isMailboxSharedOrDelegateFlag;
    }

    return (
        isOfflinePeopleDataViaPDVEnabledAllUp ||
        (isPdvInMemoryEnabledFlag && !isMailboxSharedOrDelegateFlag)
    );
}
